import Box from '@mui/material/Box';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { Virtuoso } from 'react-virtuoso';

import { Order } from '~hooks/useOrders/models';
import { useStores } from '~store';

import { ORDER_DISPATCH_CARDS_CONTAINER_GUTTER_SIZE } from './constants';
import OrderDispatchCard from './OrderDispatchCard';
import OrdersListFooter from './OrdersListFooter';

// Roughly an expanded card with a few jobs. Erring tall mounts too few cards on the
// first render and adds more, instead of mounting extras that start a jobs fetch and
// unmount (aborting it) once measured.
const EXPANDED_ORDER_CARD_HEIGHT_ESTIMATE_PX = 500;

export interface OrdersListProps {
  checkedOrdersAndJobs: Record<string, string[]>;
  companyHasChildCompanies: boolean;
  onAcceptOrder: (orderId: string) => void;
  onBulkAssignMultipleClick?: (order: Order) => void;
  onCheckedStateChange: (orderId: string, jobId?: string) => void;
  onCloneOrderClick: (order: Order) => void;
  onCopyAndEditClick: (order: Order) => void;
  onCreateJobFromOrderClick: (orderId: string) => void;
  onDownloadFilesClick?: (order: Order) => void;
  onEditClick: (order: Order) => void;
  onEmailLoadDigestClick?: (order: Order) => void;
  onExpandedStateChange: (orderId: string) => void;
  onLoadMore: () => void;
  onOrderStateChange: (orderId: string, nextOrderState: unknown) => Promise<void>;
  onRejectOrder: (order: Order) => void;
  onShowAllDates?: () => void;
  onTextAllDriversClick: (order: Order) => void;
  selectedDateRangeLabel?: string;
}

const OrdersList = observer(
  ({
    checkedOrdersAndJobs,
    companyHasChildCompanies,
    onAcceptOrder,
    onBulkAssignMultipleClick,
    onCheckedStateChange,
    onCloneOrderClick,
    onCopyAndEditClick,
    onCreateJobFromOrderClick,
    onDownloadFilesClick,
    onEditClick,
    onEmailLoadDigestClick,
    onExpandedStateChange,
    onLoadMore,
    onOrderStateChange,
    onRejectOrder,
    onShowAllDates,
    onTextAllDriversClick,
    selectedDateRangeLabel,
  }: OrdersListProps) => {
    const { ordersDispatchStore } = useStores();

    const { orders, hasMore, isLoadingOrders: isLoading } = ordersDispatchStore;

    // Add 1 to the item count to reserve space for the loading indicator or the divider with text
    const itemCount = orders.length + 1;

    const handleEndReached = useCallback(() => {
      if (hasMore && !isLoading) {
        onLoadMore();
      }
    }, [hasMore, isLoading, onLoadMore]);

    const renderOrderItem = (index: number) => {
      const order = orders[index];

      if (index >= orders.length) {
        return (
          <OrdersListFooter
            onShowAllDates={onShowAllDates}
            selectedDateRangeLabel={selectedDateRangeLabel}
          />
        );
      }

      return (
        <Box sx={{ px: ORDER_DISPATCH_CARDS_CONTAINER_GUTTER_SIZE, py: 1 }}>
          {order && (
            <OrderDispatchCard
              key={order.id}
              order={order}
              readOnly={false}
              companyHasChildCompanies={companyHasChildCompanies}
              expandedState={
                ordersDispatchStore.orderCardsExpandedState.cardsByOrder.get(order.id) ??
                ordersDispatchStore.orderCardsExpandedState.allCards
              }
              checkedJobs={checkedOrdersAndJobs[order.id]}
              onBulkAssignMultipleClick={() => onBulkAssignMultipleClick?.(order)}
              onAcceptOrder={() => onAcceptOrder(order.id)}
              onCheckedStateChange={onCheckedStateChange}
              onCloneOrderClick={() => onCloneOrderClick(order)}
              onCopyAndEditClick={() => onCopyAndEditClick(order)}
              onCreateJobFromOrderClick={() => onCreateJobFromOrderClick(order.id)}
              onDownloadFilesClick={
                onDownloadFilesClick ? () => onDownloadFilesClick(order) : undefined
              }
              onEditClick={() => onEditClick(order)}
              onEmailLoadDigestClick={
                onEmailLoadDigestClick ? () => onEmailLoadDigestClick(order) : undefined
              }
              onExpandedStateChange={onExpandedStateChange}
              onOrderStateChange={(nextOrderState) => {
                return onOrderStateChange(order.id, nextOrderState);
              }}
              onRejectOrder={() => onRejectOrder(order)}
              onTextAllDriversClick={() => onTextAllDriversClick(order)}
            />
          )}
        </Box>
      );
    };

    return (
      <Virtuoso
        computeItemKey={(index) => orders[index]?.id ?? 'footer'}
        data={Array.from({ length: itemCount }, (_, index) => index)}
        defaultItemHeight={EXPANDED_ORDER_CARD_HEIGHT_ESTIMATE_PX}
        endReached={handleEndReached}
        itemContent={renderOrderItem}
        style={{ height: '100%' }}
      />
    );
  },
);

export default OrdersList;
